import { useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { Sparkle } from "@phosphor-icons/react";

export default function DraftDialog({ draft, onClose }) {
  const [busy, setBusy] = useState(false);
  if (!draft) return null;
  const entries = draft.entries || [];
  const lines = draft.lines || [];
  const totalDr = entries.reduce((s, e) => s + Number(e.dr || 0), 0);

  const post = async () => {
    setBusy(true);
    try {
      if (["Sales", "Purchase"].includes(draft.type) && lines.length && draft.party_id) {
        const kind = draft.type === "Sales" ? "sales" : "purchase";
        const { data } = await api.post(`/invoices/${kind}`, {
          party_id: draft.party_id,
          date: draft.date,
          narration: draft.narration,
          lines: lines.map((l) => ({
            item_id: l.item_id || null,
            item_name: l.item_name,
            hsn: l.hsn || null,
            qty: Number(l.qty || 1),
            rate: Number(l.rate || 0),
            gst_rate: Number(l.gst_rate ?? 18),
          })),
        });
        toast.success(`${draft.type} ${data.number} posted`);
      } else {
        const { data } = await api.post("/vouchers", {
          type: draft.type || "Journal",
          date: draft.date,
          narration: draft.narration,
          party_id: draft.party_id || null,
          entries: entries.map((e) => ({ ledger_id: e.ledger_id, dr: Number(e.dr || 0), cr: Number(e.cr || 0) })),
        });
        toast.success(`${data.type} ${data.number} posted`);
      }
      onClose();
      window.dispatchEvent(new Event("fx:refresh"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="ai-draft-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Sparkle size={18} className="text-primary" /> AI voucher draft — human approval required
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-3 gap-3 border border-border p-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Type</p>
              <p data-testid="draft-type">{draft.type}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Date</p>
              <p className="num">{draft.date}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Confidence</p>
              <p className="num">{Math.round((draft.confidence || 0) * 100)}%</p>
            </div>
          </div>
          {draft.party_name ? <p className="text-xs text-muted-foreground">Party: {draft.party_name}</p> : null}
          {draft.narration ? <p className="text-xs text-muted-foreground">Narration: {draft.narration}</p> : null}

          {lines.length > 0 && (
            <table className="dense-table w-full">
              <thead>
                <tr><th>Item</th><th>HSN</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">GST %</th></tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td>{l.item_name}</td>
                    <td className="num">{l.hsn || "—"}</td>
                    <td className="num text-right">{l.qty}</td>
                    <td className="num text-right">{money(l.rate)}</td>
                    <td className="num text-right">{l.gst_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {entries.length > 0 && (
            <table className="dense-table w-full">
              <thead>
                <tr><th>Ledger</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i}>
                    <td>{e.ledger_name}</td>
                    <td className="num text-right">{e.dr ? money(e.dr) : "—"}</td>
                    <td className="num text-right">{e.cr ? money(e.cr) : "—"}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td>Total</td>
                  <td className="num text-right">{money(totalDr)}</td>
                  <td className="num text-right">{money(entries.reduce((s, e) => s + Number(e.cr || 0), 0))}</td>
                </tr>
              </tbody>
            </table>
          )}
          {(draft.assumptions || []).length > 0 && (
            <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
              {draft.assumptions.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="draft-discard-btn">Discard</Button>
          <Button onClick={post} disabled={busy} data-testid="draft-approve-btn">
            {busy ? "Posting…" : "Approve & post"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
